import React, { useState } from 'react';
import { Plus, X, Edit3, Save, Package, DollarSign, TrendingUp } from 'lucide-react';

interface StockItem {
  id: string;
  designation: string;
  categorie: 'matiere_premiere' | 'produit_semi_fini' | 'produit_fini' | 'fourniture' | 'emballage' | 'autre';
  description?: string;
  
  // Quantités et unités
  quantiteStock: number;
  unite: string; // kg, litres, pièces, m², etc.
  seuilMinimum?: number; // Seuil d'alerte
  seuilMaximum?: number; // Capacité maximale
  
  // Valeurs financières (actifs circulants)
  coutUnitaire: number; // Coût unitaire d'acquisition
  valeurTotaleStock: number; // Quantité × Coût unitaire
  devise: 'USD' | 'CDF' | 'EUR';
  
  // Informations temporelles et rotation
  dateDernierInventaire?: string;
  dureeRotationMoyenne?: number; // En jours
  datePeremption?: string; // Pour les produits périssables
  
  // Localisation et stockage
  emplacement?: string; // Entrepôt, magasin, etc.
  conditionsStockage?: string; // Température, humidité, etc.
  
  // Suivi et gestion
  fournisseurPrincipal?: string;
  numeroLot?: string;
  codeArticle?: string;
  
  // État et observations
  etatStock: 'excellent' | 'bon' | 'moyen' | 'deteriore' | 'perime';
  observations?: string;
}

interface StockInputProps {
  label: string;
  value: StockItem[];
  onSave: (stocks: StockItem[]) => void;
  disabled?: boolean;
  placeholder?: string;
}

export const StockInput: React.FC<StockInputProps> = ({
  label,
  value = [],
  onSave,
  disabled = false,
  placeholder = "Aucun stock enregistré"
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [stocks, setStocks] = useState<StockItem[]>(value);
  const [newStock, setNewStock] = useState<Partial<StockItem>>({
    categorie: 'matiere_premiere',
    quantiteStock: 0,
    unite: 'pièces',
    coutUnitaire: 0,
    valeurTotaleStock: 0,
    devise: 'USD',
    etatStock: 'bon',
    dateDernierInventaire: new Date().toISOString().split('T')[0]
  });

  const handleStartEditing = () => {
    setIsEditing(true);
    setStocks([...value]);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setStocks([...value]);
    resetNewStock();
  };

  const handleSaveEditing = () => {
    onSave(stocks);
    setIsEditing(false);
    resetNewStock();
  };

  const resetNewStock = () => {
    setNewStock({
      categorie: 'matiere_premiere',
      quantiteStock: 0,
      unite: 'pièces',
      coutUnitaire: 0,
      valeurTotaleStock: 0,
      devise: 'USD',
      etatStock: 'bon',
      dateDernierInventaire: new Date().toISOString().split('T')[0]
    });
  };

  const handleAddStock = () => {
    if (newStock.designation && newStock.categorie) {
      const stockItem: StockItem = {
        id: Date.now().toString(),
        designation: newStock.designation || '',
        categorie: newStock.categorie || 'matiere_premiere',
        description: newStock.description || '',
        quantiteStock: newStock.quantiteStock || 0,
        unite: newStock.unite || 'pièces',
        seuilMinimum: newStock.seuilMinimum,
        seuilMaximum: newStock.seuilMaximum,
        coutUnitaire: newStock.coutUnitaire || 0,
        valeurTotaleStock: (newStock.quantiteStock || 0) * (newStock.coutUnitaire || 0),
        devise: newStock.devise || 'USD',
        dateDernierInventaire: newStock.dateDernierInventaire || new Date().toISOString().split('T')[0],
        dureeRotationMoyenne: newStock.dureeRotationMoyenne,
        datePeremption: newStock.datePeremption,
        emplacement: newStock.emplacement || '',
        conditionsStockage: newStock.conditionsStockage || '',
        fournisseurPrincipal: newStock.fournisseurPrincipal || '',
        numeroLot: newStock.numeroLot || '',
        codeArticle: newStock.codeArticle || '',
        etatStock: newStock.etatStock || 'bon',
        observations: newStock.observations || ''
      };
      setStocks([...stocks, stockItem]);
      resetNewStock();
    }
  };

  const handleRemoveStock = (index: number) => {
    const updatedStocks = stocks.filter((_, i) => i !== index);
    setStocks(updatedStocks);
  };

  const handleEditStock = (index: number, field: keyof StockItem, value: any) => {
    const updatedStocks = [...stocks];
    updatedStocks[index] = { ...updatedStocks[index], [field]: value };
    
    // Recalculer la valeur totale si quantité ou coût change
    if (field === 'quantiteStock' || field === 'coutUnitaire') {
      const stock = updatedStocks[index];
      updatedStocks[index].valeurTotaleStock = (stock.quantiteStock || 0) * (stock.coutUnitaire || 0);
    }
    
    setStocks(updatedStocks);
  };

  const getCategorieLabel = (categorie: string) => {
    switch (categorie) {
      case 'matiere_premiere': return 'Matière première';
      case 'produit_semi_fini': return 'Produit semi-fini';
      case 'produit_fini': return 'Produit fini';
      case 'fourniture': return 'Fourniture';
      case 'emballage': return 'Emballage';
      default: return 'Autre';
    }
  };

  const getCategorieColor = (categorie: string) => {
    switch (categorie) {
      case 'matiere_premiere': return 'bg-blue-100 text-blue-800';
      case 'produit_semi_fini': return 'bg-yellow-100 text-yellow-800';
      case 'produit_fini': return 'bg-green-100 text-green-800';
      case 'fourniture': return 'bg-purple-100 text-purple-800';
      case 'emballage': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEtatStockColor = (etat: string) => {
    switch (etat) {
      case 'excellent': return 'bg-emerald-100 text-emerald-800';
      case 'bon': return 'bg-green-100 text-green-800';
      case 'moyen': return 'bg-yellow-100 text-yellow-800';
      case 'deteriore': return 'bg-orange-100 text-orange-800';
      case 'perime': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStockIcon = () => {
    return <Package className="w-4 h-4" />;
  };

  if (!isEditing) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            {label}
          </label>
          {!disabled && (
            <button
              onClick={handleStartEditing}
              className="text-[#197ca8] hover:text-[#156a92] text-sm flex items-center"
            >
              <Edit3 className="w-4 h-4 mr-1" />
              Modifier
            </button>
          )}
        </div>
        
        {value.length === 0 ? (
          <div className="text-gray-500 text-sm italic p-4 border-2 border-dashed border-gray-200 rounded-lg text-center">
            {placeholder}
          </div>
        ) : (
          <div className="space-y-3">
            {value.map((stock) => (
              <div key={stock.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className="text-[#197ca8] mr-2">
                        {getStockIcon()}
                      </span>
                      <span className="font-medium text-gray-900">
                        {stock.designation}
                      </span>
                      <span className={`ml-2 px-2 py-1 text-xs rounded ${getCategorieColor(stock.categorie)}`}>
                        {getCategorieLabel(stock.categorie)}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm text-gray-600">
                      {stock.description && (
                        <div className="col-span-4 text-gray-700 mb-2">
                          {stock.description}
                        </div>
                      )}
                      
                      <div className="flex flex-col space-y-1">
                        <div>
                          <span className="font-medium">Quantité:</span> {stock.quantiteStock} {stock.unite}
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${getEtatStockColor(stock.etatStock)}`}>
                          {stock.etatStock}
                        </span>
                      </div>

                      <div className="flex flex-col space-y-1">
                        <div className="flex items-center text-gray-600">
                          <span className="font-medium">Coût unitaire:</span>
                          <span className="ml-1">{stock.coutUnitaire.toLocaleString()} {stock.devise}</span>
                        </div>
                        <div className="flex items-center text-[#197ca8] font-medium">
                          <DollarSign className="w-3 h-3 mr-1" />
                          <span>Valeur totale: {stock.valeurTotaleStock.toLocaleString()} {stock.devise}</span>
                        </div>
                      </div>

                      {stock.dateDernierInventaire && (
                        <div>
                          <span className="font-medium text-gray-500">Dernier inventaire:</span><br />
                          {new Date(stock.dateDernierInventaire).toLocaleDateString('fr-FR')}
                        </div>
                      )}

                      <div className="flex flex-col space-y-1">
                        {stock.emplacement && (
                          <div>
                            <span className="font-medium">Emplacement:</span> {stock.emplacement}
                          </div>
                        )}
                        {stock.fournisseurPrincipal && (
                          <div>
                            <span className="font-medium">Fournisseur:</span> {stock.fournisseurPrincipal}
                          </div>
                        )}
                        {stock.dureeRotationMoyenne && (
                          <div className="flex items-center text-blue-600">
                            <TrendingUp className="w-3 h-3 mr-1" />
                            <span>Rotation: {stock.dureeRotationMoyenne} jours</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleCancelEditing}
            className="text-gray-600 hover:text-gray-800 text-sm flex items-center"
          >
            <X className="w-4 h-4 mr-1" />
            Annuler
          </button>
          <button
            onClick={handleSaveEditing}
            className="text-green-600 hover:text-green-800 text-sm flex items-center"
          >
            <Save className="w-4 h-4 mr-1" />
            Sauvegarder
          </button>
        </div>
      </div>

      {/* Liste des stocks existants */}
      <div className="space-y-3">
        {stocks.map((stock, index) => (
          <div key={stock.id} className="bg-white border-2 border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <span className="text-[#197ca8] mr-2">
                  {getStockIcon()}
                </span>
                <span className="font-medium text-gray-900">
                  Stock #{index + 1}
                </span>
              </div>
              <button
                onClick={() => handleRemoveStock(index)}
                className="text-red-600 hover:text-red-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Désignation *</label>
                <input
                  type="text"
                  value={stock.designation}
                  onChange={(e) => handleEditStock(index, 'designation', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Catégorie *</label>
                <select
                  value={stock.categorie}
                  onChange={(e) => handleEditStock(index, 'categorie', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                >
                  <option value="matiere_premiere">Matière première</option>
                  <option value="produit_semi_fini">Produit semi-fini</option>
                  <option value="produit_fini">Produit fini</option>
                  <option value="fourniture">Fourniture</option>
                  <option value="emballage">Emballage</option>
                  <option value="autre">Autre</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Quantité en stock</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={stock.quantiteStock}
                  onChange={(e) => handleEditStock(index, 'quantiteStock', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Unité</label>
                <input
                  type="text"
                  value={stock.unite}
                  onChange={(e) => handleEditStock(index, 'unite', e.target.value)}
                  placeholder="pièces, kg, litres..."
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Coût unitaire</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={stock.coutUnitaire}
                  onChange={(e) => handleEditStock(index, 'coutUnitaire', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Valeur totale</label>
                <input
                  type="number"
                  value={stock.valeurTotaleStock}
                  readOnly
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-md bg-gray-50 text-gray-600 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Devise</label>
                <select
                  value={stock.devise}
                  onChange={(e) => handleEditStock(index, 'devise', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                >
                  <option value="USD">USD</option>
                  <option value="CDF">CDF</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Dernier inventaire</label>
                <input
                  type="date"
                  value={stock.dateDernierInventaire || ''}
                  onChange={(e) => handleEditStock(index, 'dateDernierInventaire', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
                />
              </div>
            </div>

            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={stock.description}
                onChange={(e) => handleEditStock(index, 'description', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Ajouter un nouveau stock */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-3">
          Ajouter un nouveau stock
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Désignation *</label>
            <input
              type="text"
              value={newStock.designation || ''}
              onChange={(e) => setNewStock({...newStock, designation: e.target.value})}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Catégorie *</label>
            <select
              value={newStock.categorie || 'matiere_premiere'}
              onChange={(e) => setNewStock({...newStock, categorie: e.target.value as any})}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
            >
              <option value="matiere_premiere">Matière première</option>
              <option value="produit_semi_fini">Produit semi-fini</option>
              <option value="produit_fini">Produit fini</option>
              <option value="fourniture">Fourniture</option>
              <option value="emballage">Emballage</option>
              <option value="autre">Autre</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Quantité</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={newStock.quantiteStock || ''}
              onChange={(e) => {
                const quantite = parseFloat(e.target.value) || 0;
                setNewStock({
                  ...newStock, 
                  quantiteStock: quantite,
                  valeurTotaleStock: quantite * (newStock.coutUnitaire || 0)
                });
              }}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Coût unitaire</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={newStock.coutUnitaire || ''}
              onChange={(e) => {
                const cout = parseFloat(e.target.value) || 0;
                setNewStock({
                  ...newStock, 
                  coutUnitaire: cout,
                  valeurTotaleStock: (newStock.quantiteStock || 0) * cout
                });
              }}
              className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
            />
          </div>
        </div>

        <div className="mt-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={newStock.description || ''}
            onChange={(e) => setNewStock({...newStock, description: e.target.value})}
            rows={2}
            placeholder="Description du stock, spécifications..."
            className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] text-sm"
          />
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={handleAddStock}
            disabled={!newStock.designation || !newStock.categorie}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#197ca8] hover:bg-[#156a92] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#197ca8] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
};